import {
  JsonNetworkSuccess,
  JsonApiNetworkSuccess,
  JsonApiResponse,
  JsonApiResource,
  JsonApiResourceDto,
  JsonApiRelationships,
  IncludedMap,
} from './types';
import {
  NetworkError,
  UnauthorizedError,
  BadRequestError,
  ForbiddenError,
  VersionConflictError,
  InternalServerError,
} from './errors';

export enum ContentTypes {
  Json = 'application/json',
  JsonApi = 'application/vnd.api+json',
  Pdf = 'application/pdf',
}

function buildResourceRelationshipAttributes(
  relationships: JsonApiRelationships,
  includedMap: IncludedMap
): JsonApiResource<any> {
  const defaultValue: JsonApiResource<any> = {};

  return relationships && includedMap
    ? Object.keys(relationships).reduce(
        (result: JsonApiResource<any>, relationshipName) => {
          const relationshipData = relationships[relationshipName].data;
          const relationshipDataExists = result[relationshipName];

          if (!relationshipDataExists) {
            if (Array.isArray(relationshipData)) {
              result[relationshipName] = relationshipData.map(r => {
                const includedResource = includedMap[r.type][r.id];

                return includedResource
                  ? // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    mapResource(includedResource, includedMap)
                  : undefined; //Should we just keep the relationship data defined here?
              });
            } else if (relationshipData) {
              const includedResource =
                includedMap[relationshipData.type][relationshipData.id];

              result[relationshipName] = includedResource
                ? // eslint-disable-next-line @typescript-eslint/no-use-before-define
                  mapResource(includedResource, includedMap)
                : undefined; //Should we just keep the relationship data defined here?
            }
          }

          return result;
        },
        defaultValue
      )
    : defaultValue;
}

function mapResource<Attributes>(
  rawResource: JsonApiResourceDto<Attributes, any, any>,
  includedMap: IncludedMap
): { id: string } & Attributes {
  const { id, attributes, relationships } = rawResource;
  const relationshipAttributes = buildResourceRelationshipAttributes(
    relationships,
    includedMap
  );

  return {
    id,
    ...attributes,
    ...relationshipAttributes,
  };
}

function buildIncludedMap(
  included: Array<JsonApiResourceDto<any, any, any>>
): IncludedMap {
  return included.reduce((result: IncludedMap, item) => {
    const itemType = item.type;
    const itemId = item.id;

    result[itemType] = result[itemType] || {};
    result[itemType][itemId] = result[itemType][itemId] || item;

    return result;
  }, {});
}

function transformJsonApiResponse<Attributes extends Array<any>>(
  json: JsonApiResponse<Attributes, any, void>
): { data: Attributes; included: any; meta: any };
function transformJsonApiResponse<Attributes>(
  json: JsonApiResponse<Attributes, any, void>
): { data: Attributes; included: any; meta: any };
function transformJsonApiResponse<Attributes>(
  json: JsonApiResponse<Attributes, any, void>
) {
  const { data, included, meta } = json;
  const includedMap = buildIncludedMap(included || []);

  let result;

  if (Array.isArray(data)) {
    result = data.map(d => mapResource<Attributes>(d, includedMap));
  } else {
    result = mapResource<Attributes>(data, includedMap);
  }

  return {
    data: result,
    included: included?.map(d => mapResource(d, includedMap)),
    meta,
  };
}

async function handleResponseError(response: Response) {
  if (!response.ok) {
    let error;
    let jsonBody;

    try {
      jsonBody = await response.json();
    } catch (_) {}

    switch (response.status) {
      case 400:
        error = new BadRequestError(undefined, jsonBody, response);
        break;
      case 401:
        error = new UnauthorizedError(undefined, response);
        break;
      case 403:
        error = new ForbiddenError(undefined, response);
        break;
      case 409:
        error = new VersionConflictError(undefined, jsonBody, response);
        break;
      case 500:
        error = new InternalServerError(undefined, response);
        break;
      default:
        error = new NetworkError(undefined, response);
    }

    throw error;
  }
}

export async function handleJsonApiResponse<T>(
  response: Response
): Promise<JsonApiNetworkSuccess<T>> {
  await handleResponseError(response);

  const headers = response.headers;
  const rawJson: JsonApiResponse<T, any, any> | null =
    response.status === 204 ? null : await response.json();
  const json = rawJson
    ? transformJsonApiResponse<T>(rawJson)
    : { data: {} as T, meta: undefined, included: undefined };

  return {
    headers,
    json: json.data,
    included: json.included,
    meta: json.meta,
  };
}
export async function handleResponse<T>(
  response: Response
): Promise<JsonNetworkSuccess<T>> {
  await handleResponseError(response);

  const headers = response.headers;

  let json;

  try {
    json = response.status === 204 ? null : await response.json();
  } catch (e) {}

  return {
    headers,
    json,
    response,
  };
}

export function generateCacheKey(input: string | null, token: string | null) {
  return token && input ? [input, token] : null;
}

// Given a url like '/api/v1/documents/foo?documentId={documentId}', replaces
// placeholders in brackets with the passed parameters.
export function replaceUrlParams(
  url: string,
  ...params: Array<string | boolean | number>
) {
  return params.reduce(
    (result: string, param) => result.replace(/{[a-zA-Z]*}/, param.toString()),
    url
  );
}

export async function makeJsonApiRequest<T>(
  input: string,
  token: string,
  overrideInit?: RequestInit,
  abortController?: AbortController
): Promise<JsonApiNetworkSuccess<T>> {
  const options = {
    ...overrideInit,
    headers: {
      ...(overrideInit?.headers ?? {}),
      Authorization: `Bearer ${token}`,
      Accept: ContentTypes.JsonApi,
      ['Content-Type']: ContentTypes.JsonApi,
    },
  };
  const encodedInput = encodeURI(input);
  const response = await fetch(encodedInput, {
    ...options,
    signal: abortController?.signal,
  });

  return handleJsonApiResponse<T>(response);
}
export async function makeRequest<T>(
  input: string,
  token: string,
  overrideInit?: RequestInit,
  abortController?: AbortController
): Promise<JsonNetworkSuccess<T>> {
  const options = {
    ...overrideInit,
    headers: {
      ...(overrideInit?.headers ?? {}),
      Accept: ContentTypes.Json,
      Authorization: `Bearer ${token}`,
      ['Content-Type']: ContentTypes.Json,
    },
  };
  const encodedInput = encodeURI(input);
  const response = await fetch(encodedInput, {
    ...options,
    signal: abortController?.signal,
  });

  return handleResponse<T>(response);
}

function postInit(body: undefined | Record<string, any>) {
  return {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  };
}
function patchInit(body: undefined | Record<string, any>) {
  return {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  };
}
function deleteInit() {
  return {
    method: 'DELETE',
  };
}

export async function jsonApiGet<T>(
  url: string,
  token: string
): Promise<JsonApiNetworkSuccess<T>> {
  return makeJsonApiRequest(url, token);
}

export async function jsonGet<T>(
  url: string,
  token: string
): Promise<JsonNetworkSuccess<T>> {
  return makeRequest(url, token);
}

export async function jsonApiPost<T>(
  url: string,
  token: string,
  body?: Record<string, any>
): Promise<JsonApiNetworkSuccess<T>> {
  const init = postInit(body);

  return makeJsonApiRequest(url, token, init);
}
export async function jsonPost<T>(
  url: string,
  token: string,
  body?: Record<string, any>,
  headers?: Record<string, any>
): Promise<JsonNetworkSuccess<T>> {
  const init = postInit(body);

  return makeRequest(url, token, { ...init, headers });
}

export async function jsonApiPatch<T>(
  url: string,
  token: string,
  body?: Record<string, any>
): Promise<JsonApiNetworkSuccess<T>> {
  const init = patchInit(body);

  return makeJsonApiRequest(url, token, init);
}
export async function jsonPatch<T>(
  url: string,
  token: string,
  body?: Record<string, any>
): Promise<JsonNetworkSuccess<T>> {
  const init = patchInit(body);

  return makeRequest(url, token, init);
}
export async function jsonApiDelete(
  url: string,
  token: string
): Promise<JsonApiNetworkSuccess<void>> {
  const init = deleteInit();

  return makeJsonApiRequest(url, token, init);
}

// delete is a reserved word so have to use jsonDelete
export async function jsonDelete(
  url: string,
  token: string
): Promise<JsonNetworkSuccess<void>> {
  const init = deleteInit();

  return makeRequest(url, token, init);
}
